// /Users/apichet/Downloads/cheetah-insurance-app/frontend/src/context/AuthContext.js
// /src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { jwtDecode } from "jwt-decode";


import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import {
  getAuthToken,
  getRefreshToken,
  saveAuthToken,
  saveRefreshToken,
  clearTokens,
  refreshAccessToken,
  saveUserRole,
} from "../api/authService";
import { isTokenExpired } from "../api/tokenUtils";  // ✅ ใช้จาก utils

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasCheckedTokensRef = useRef(false);
  const hasRedirectedRef = useRef(false);

  const [auth, setAuth] = useState({
    user: null,
    authToken: null,
    refreshToken: null,
    role: null,
    isAdmin: false,
  });

  const [isAuthInitializing, setIsAuthInitializing] = useState(true);

  const isAdminPanel = useMemo(
    () => location.pathname.startsWith("/admin"),
    [location.pathname]
  );

  // ตั้งค่า header บน axiosInstance (แม้อินเตอร์เซปเตอร์จะจัดการจาก storage ให้อยู่แล้ว)
  const setAuthTokenHeaders = (token) => {
    if (token) {
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.common.Authorization;
    }
  };

  const loginUser = (accessToken, refreshToken, role) => {
    if (!accessToken || !refreshToken) {
      throw new Error("Missing access/refresh token");
    }

    saveAuthToken(accessToken);
    saveRefreshToken(refreshToken);
    if (role) saveUserRole(role);

    const decoded = jwtDecode(accessToken);
    setAuthTokenHeaders(accessToken);
    setAuth({
      user: decoded,
      authToken: accessToken,
      refreshToken,
      role: role || decoded.role || "user",
      isAdmin: (role || decoded.role) === "admin",
    });
  };

  const logout = useCallback(async () => {
    try {
      // แจ้ง backend แต่ไม่บังคับสำเร็จ
      await axiosInstance.post("/auth/logout").catch(() => {});
    } catch (_) {}
  
    clearTokens(); // เคลียร์ localStorage
  
    // เคลียร์ header ที่ set ไว้
    delete axiosInstance.defaults.headers.common["Authorization"];
  
    setAuth({
      user: null,
      authToken: null,
      refreshToken: null,
      role: null,
      isAdmin: false,
    });
  
    if (window.location.hash !== "#/login" && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 50);
    }
  }, [navigate]);
  

  const handleTokenRefresh = useCallback(async () => {
    const rt = getRefreshToken();
    if (!rt) {
      logout();
      return false;
    }

    try {
      const newAccessToken = await refreshAccessToken();
      if (!newAccessToken) {
        logout();
        return false;
      }

      saveAuthToken(newAccessToken);
      const decoded = jwtDecode(newAccessToken);
      setAuthTokenHeaders(newAccessToken);
      setAuth((prev) => ({
        ...prev,
        user: decoded,
        authToken: newAccessToken,
        role: decoded.role || prev.role || "user",
        isAdmin: (decoded.role || prev.role) === "admin",
      }));
      return true;
    } catch {
      logout();
      return false;
    }
  }, [logout]);

  const initializeAuth = useCallback(async () => {
    if (hasCheckedTokensRef.current) return;
    hasCheckedTokensRef.current = true;

    // เส้นทาง public ที่ไม่บังคับตรวจ token
    const PUBLIC_PATHS = ["/", "/home", "/login", "/register", "/forgot-password", "/admin/login"];
    const currentPath = location.pathname;

    if (PUBLIC_PATHS.some((p) => currentPath.startsWith(p))) {
      setIsAuthInitializing(false);
      return;
    }

    const at = getAuthToken();
    const rt = getRefreshToken();

    if (!at && !rt) {
      setIsAuthInitializing(false);
      return;
    }

    if (at) {
      try {
        if (!isTokenExpired(at)) {
          const decoded = jwtDecode(at);
          setAuthTokenHeaders(at);
          setAuth({
            user: decoded,
            authToken: at,
            refreshToken: rt || null,
            role: decoded.role || "user",
            isAdmin: decoded.role === "admin",
          });
          setIsAuthInitializing(false);
          return;
        } else if (rt) {
          const refreshed = await handleTokenRefresh();
          setIsAuthInitializing(!refreshed);
          return;
        } else {
          logout();
        }
      } catch {
        logout();
      }
    }

    setIsAuthInitializing(false);
  }, [handleTokenRefresh, logout, location.pathname]);

  useEffect(() => {
    if (!hasCheckedTokensRef.current) {
      initializeAuth();
    }
  }, [initializeAuth]);

  const isAuthenticated = !!auth.authToken && !isTokenExpired(auth.authToken);

  return (
    <AuthContext.Provider
      value={{
        ...auth,
        isAuthenticated,
        isTokenExpired: isTokenExpired(auth.authToken), // ✅ boolean
        isAuthInitializing,
        loginUser,
        logout,
        isAdminPanel,
      }}
    >
      {!isAuthInitializing && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
